import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center cursor-pointer font-[var(--button-font-weight,500)] justify-center gap-2 whitespace-nowrap rounded-[var(--button-radius,var(--radius))] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none",
  {
    variants: {
      variant: {
        default:
          "bg-[var(--button-primary-bg,var(--color-primary))] text-[var(--button-primary-fg,var(--color-primary-foreground))] border border-2 border-[var(--button-primary-border,var(--color-primary))] shadow-[var(--button-primary-shadow,none)] hover:brightness-95 [&_svg]:size-4 [&_svg]:shrink-0",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 [&_svg]:size-4 [&_svg]:shrink-0",
        outline:
          "bg-[var(--button-outline-bg,var(--color-background))] text-[var(--button-outline-fg,var(--color-foreground))] border-2 border-[var(--button-outline-border,var(--color-foreground))] shadow-[var(--button-outline-shadow,0_1px_3px_0px_hsl(0_0%_40%_/_0.15))] hover:bg-[var(--button-outline-hover-bg,var(--color-accent))] hover:text-[var(--button-outline-hover-fg,var(--color-accent-foreground))] [&_svg]:size-4 [&_svg]:shrink-0",
        secondary:
          "bg-[var(--button-secondary-bg,var(--color-secondary))] text-[var(--button-secondary-fg,var(--color-secondary-foreground))] border-2 border-[var(--button-secondary-border,var(--color-secondary))] shadow-[var(--button-secondary-shadow,none)] hover:bg-[var(--button-secondary-hover-bg,var(--color-secondary))] hover:text-[var(--button-secondary-hover-fg,var(--color-secondary-foreground))] [&_svg]:size-4 [&_svg]:shrink-0",
        ghost:
          "hover:bg-[var(--button-ghost-hover-bg,var(--color-accent))] hover:text-[var(--button-ghost-hover-fg,var(--color-accent-foreground))] [&_svg]:size-4 [&_svg]:shrink-0",
        link: "relative group cursor-pointer underline font-medium",
      },
      size: {
        default: "text-sm px-5 py-3",
        xs: "h-6.5 rounded-sm px-2 text-xs",
        sm: "h-8 rounded-md px-3 text-xs",
        md: "h-10 rounded-md px-3 py-2 text-sm",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
      // Link-specific padding sizes for the background
      linkSize: {
        default: "", // Will use default padding defined in component
        sm: "", // Will use small padding defined in component
        md: "", // Will use medium padding defined in component
        lg: "", // Will use large padding defined in component
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      linkSize: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, linkSize, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";

    // Add a wrapper for link variant to create the hover effect
    if (variant === "link") {
      // Define custom link sizes independent of button sizes
      let marginClass = "-m-1"; // Default - Porsche style

      if (linkSize === "sm") {
        marginClass = "-m-0.5"; // Extra small
      } else if (linkSize === "md") {
        marginClass = "-m-1.5"; // Medium
      } else if (linkSize === "lg") {
        marginClass = "-m-2"; // Large
      }

      return (
        <Comp
          className={cn(buttonVariants({ variant, size, linkSize, className }))}
          ref={ref}
          {...props}
        >
          <span className="relative inline-flex items-center">
            {props.children}
            <span
              className={`absolute inset-0 -z-10 ${marginClass} group-hover:bg-accent rounded-sm bg-transparent`}
            ></span>
          </span>
        </Comp>
      );
    }

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, linkSize, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
